import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StepIndicator from '../components/StepIndicator';
import { useSubscriptionCatalog } from '../hooks/useSubscriptionCatalog';
import { prefetchVendors } from '../hooks/useVendors';
import useSEO from '../hooks/useSEO';
import { formatNaira } from '../lib/data';
import { GiftItem, GiftOrder, OrderItem, SubscriptionOption } from '../types';

type SubscriptionTabId = 'bread' | 'water' | 'other' | 'all' | 'food' | 'beverages';
type FilterKey = 'brand' | 'type' | 'color';

interface SubscriptionCustomizePageProps {
  selectedItems: OrderItem[];
  subscription?: GiftOrder['subscription'];
  onUpdateItems: (items: OrderItem[]) => void;
  onSubscriptionChange?: (data: GiftOrder['subscription']) => void;
  onContinue: () => void;
  onBack: () => void;
}

interface SubscriptionDisplayItem {
  id: string;
  item: GiftItem;
  tabId: SubscriptionTabId;
  brand?: string;
  type?: string;
  color?: string;
  size?: string;
  subtitle: string;
}

interface SubscriptionItemCardProps {
  displayItem: SubscriptionDisplayItem;
  quantity: number;
  isB2B: boolean;
  onUpdateQuantity: (displayItem: SubscriptionDisplayItem, nextQuantity: number) => void;
}

const TAB_ORDER: Array<{ id: SubscriptionTabId; label: string }> = [
  { id: 'bread', label: 'Bread' },
  { id: 'water', label: 'Water' },
  { id: 'other', label: 'All Items' },
];

const EMPTY_FILTERS: Record<FilterKey, string | null> = {
  brand: null,
  type: null,
  color: null,
};

const mapCategoryToTab = (category?: string): SubscriptionTabId => {
  const normalizedCategory = category?.trim().toLowerCase();

  if (normalizedCategory === 'beverages' || normalizedCategory === 'water') {
    return 'water';
  }

  if (
    normalizedCategory === 'bread' ||
    normalizedCategory === 'bakery' ||
    normalizedCategory === 'breakfast' ||
    normalizedCategory === 'groceries'
  ) {
    return 'bread';
  }

  return 'other';
};

const normalizeFilterValue = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const normalizeBrandValue = (value?: string | string[] | null): string | undefined => {
  if (Array.isArray(value)) {
    return normalizeFilterValue(value.find((entry) => normalizeFilterValue(entry)));
  }

  return normalizeFilterValue(value ?? undefined);
};

const normalizeItemTypeValue = (
  typeValue?: string | null,
  typeOptions?: string[] | null
): string | undefined => {
  const optionValue = typeOptions?.find((entry) => normalizeFilterValue(entry));
  if (optionValue) {
    return normalizeFilterValue(optionValue);
  }

  const normalizedType = normalizeFilterValue(typeValue);
  if (normalizedType && normalizedType !== 'product' && normalizedType !== 'subscription') {
    return normalizedType;
  }

  return undefined;
};

const titleCase = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  return value
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const buildVariantSummary = ({
  brand,
  type,
  color,
  fallback,
}: {
  brand?: string;
  type?: string;
  color?: string;
  fallback?: string;
}): string => {
  const parts = [brand, type, color]
    .map((value) => titleCase(normalizeFilterValue(value)))
    .filter((value): value is string => Boolean(value));

  if (parts.length > 0) {
    return parts.join(' - ');
  }

  return fallback?.trim() || 'Plan item';
};

const buildSubscriptionOption = (
  item: GiftItem,
  frequency?: string,
  detailLabel?: string
): SubscriptionOption => ({
  name: frequency || 'subscription',
  price: item.price ?? 0,
  quantity: detailLabel || 'Plan item',
});

const SubscriptionItemCard = React.memo(function SubscriptionItemCard({
  displayItem,
  quantity,
  isB2B,
  onUpdateQuantity,
}: SubscriptionItemCardProps) {
  const unitPrice = displayItem.item.price ?? 0;
  const itemCategoryLabel = displayItem.tabId === 'beverages' || displayItem.tabId === 'water'
    ? 'Beverage'
    : 'Food';

  if (isB2B) {
    return (
      <article className="subscription-item-card subscription-item-card-b2b">
        <div className="subscription-item-main-b2b">
          <div className="subscription-item-copy subscription-item-copy-b2b">
            <h2>{displayItem.item.name}</h2>
            <p>{itemCategoryLabel}</p>
            <strong>{formatNaira(unitPrice)}/piece</strong>
          </div>

          <div className="subscription-quantity-control subscription-quantity-control-b2b">
            <button
              type="button"
              className="subscription-quantity-button subscription-quantity-step muted"
              onClick={() => onUpdateQuantity(displayItem, quantity - 10)}
              aria-label={`Remove ten ${displayItem.item.name}`}
            >
              -10
            </button>
            <button
              type="button"
              className="subscription-quantity-button muted"
              onClick={() => onUpdateQuantity(displayItem, quantity - 1)}
              aria-label={`Remove one ${displayItem.item.name}`}
            >
              -
            </button>
            <span>{quantity}</span>
            <button
              type="button"
              className="subscription-quantity-button muted"
              onClick={() => onUpdateQuantity(displayItem, quantity + 1)}
              aria-label={`Add one more ${displayItem.item.name}`}
            >
              +
            </button>
            <button
              type="button"
              className="subscription-quantity-button subscription-quantity-step"
              onClick={() => onUpdateQuantity(displayItem, quantity + 10)}
              aria-label={`Add ten more ${displayItem.item.name}`}
            >
              +10
            </button>
          </div>
        </div>

        <div className="subscription-b2b-subtotal">
          Daily subtotal: <strong>{formatNaira(unitPrice * quantity)}</strong>
        </div>
      </article>
    );
  }

  return (
    <article className="subscription-item-card subscription-item-card-consumer">
      <div className="subscription-item-main-consumer">
        <div className="subscription-item-copy subscription-item-copy-consumer">
          <h2>{displayItem.item.name}</h2>
          <p>{displayItem.subtitle}</p>
          <strong>{formatNaira(unitPrice)}</strong>
        </div>

        {quantity > 0 ? (
          <div className="subscription-quantity-control subscription-quantity-control-consumer">
            <button
              type="button"
              className="subscription-quantity-button muted"
              onClick={() => onUpdateQuantity(displayItem, quantity - 1)}
              aria-label={`Remove one ${displayItem.item.name}`}
            >
              -
            </button>
            <span>{quantity}</span>
            <button
              type="button"
              className="subscription-quantity-button"
              onClick={() => onUpdateQuantity(displayItem, quantity + 1)}
              aria-label={`Add one more ${displayItem.item.name}`}
            >
              +
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="subscription-add-button subscription-add-button-consumer"
            onClick={() => onUpdateQuantity(displayItem, 1)}
          >
            Add
          </button>
        )}
      </div>
    </article>
  );
});

export default function SubscriptionCustomizePage({
  selectedItems,
  subscription,
  onUpdateItems,
  onSubscriptionChange,
  onContinue,
  onBack,
}: SubscriptionCustomizePageProps) {
  const { items: catalogItems, loading, error, refetch } = useSubscriptionCatalog();
  const isB2B = subscription?.category === 'b2b';
  const [activeTab, setActiveTab] = useState<SubscriptionTabId>(isB2B ? 'all' : 'bread');
  const [filters, setFilters] = useState<Record<FilterKey, string | null>>(EMPTY_FILTERS);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [draftItems, setDraftItems] = useState<OrderItem[]>(selectedItems);

  useSEO({
    title: isB2B ? 'Build Your Daily Mix - BOQBOX' : 'Customize Your Plan - BOQBOX',
    description: isB2B
      ? 'Adjust quantities within your reseller tier before checkout.'
      : 'Adjust your subscription package before continuing to checkout.',
  });

  useEffect(() => {
    setDraftItems(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    setActiveTab(isB2B ? 'all' : 'bread');
  }, [isB2B]);

  useEffect(() => {
    if (
      isB2B &&
      subscription &&
      onSubscriptionChange &&
      !subscription.deliveryFrequency
    ) {
      onSubscriptionChange({
        ...subscription,
        deliveryFrequency: 'Daily',
      });
    }
  }, [isB2B, onSubscriptionChange, subscription]);

  const b2bDeliveryFrequency = isB2B ? subscription?.deliveryFrequency || 'Daily' : '';

  const packageMetaById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<NonNullable<GiftOrder['subscription']>['packageItems']>[number]
    >();

    for (const packageItem of subscription?.packageItems ?? []) {
      map.set(packageItem.item._id, packageItem);
    }

    return map;
  }, [subscription?.packageItems]);

  const normalizedItems = useMemo(() => {
    const displayItems = new Map<string, SubscriptionDisplayItem>();

    for (const catalogItem of catalogItems) {
      const id = catalogItem._id || catalogItem.id;
      if (!id) {
        continue;
      }

      const packageMeta = packageMetaById.get(id);
      const brand =
        normalizeBrandValue(catalogItem.brand) ?? normalizeFilterValue(packageMeta?.brand);
      const type =
        normalizeItemTypeValue(catalogItem.type, catalogItem.types) ??
        normalizeFilterValue(packageMeta?.type);
      const color = normalizeFilterValue(packageMeta?.color);
      const size = normalizeFilterValue(packageMeta?.size);

      displayItems.set(id, {
        id,
        item: catalogItem,
        tabId: isB2B
          ? (mapCategoryToTab(catalogItem.category) === 'water' ? 'beverages' : 'food')
          : mapCategoryToTab(catalogItem.category),
        brand,
        type,
        color,
        size,
        subtitle: buildVariantSummary({
          brand,
          type,
          color,
          fallback: catalogItem.description,
        }),
      });
    }

    for (const selectedItem of draftItems) {
      const id = selectedItem.item._id || selectedItem.item.id;
      if (!id || displayItems.has(id)) {
        continue;
      }

      const packageMeta = packageMetaById.get(id);
      const brand =
        normalizeBrandValue(selectedItem.item.brand) ?? normalizeFilterValue(packageMeta?.brand);
      const type =
        normalizeItemTypeValue(selectedItem.item.type, selectedItem.item.types) ??
        normalizeFilterValue(packageMeta?.type);
      const color = normalizeFilterValue(packageMeta?.color);
      const size = normalizeFilterValue(packageMeta?.size);
      const itemCategory =
        selectedItem.item.category && selectedItem.item.category !== 'subscription'
          ? selectedItem.item.category
          : undefined;

      displayItems.set(id, {
        id,
        item: selectedItem.item,
        tabId: isB2B
          ? (mapCategoryToTab(itemCategory) === 'water' ? 'beverages' : 'food')
          : mapCategoryToTab(itemCategory),
        brand,
        type,
        color,
        size,
        subtitle: buildVariantSummary({
          brand,
          type,
          color,
          fallback: selectedItem.item.description,
        }),
      });
    }

    return Array.from(displayItems.values());
  }, [catalogItems, draftItems, isB2B, packageMetaById]);

  const availableTabs = useMemo(() => {
    if (isB2B) {
      return [
        { id: 'all' as SubscriptionTabId, label: 'All Items' },
        { id: 'food' as SubscriptionTabId, label: 'Food Items' },
        { id: 'beverages' as SubscriptionTabId, label: 'Beverages' },
      ];
    }

    const tabSet = new Set<SubscriptionTabId>(normalizedItems.map((item) => item.tabId));
    return TAB_ORDER.filter((tab) => tabSet.has(tab.id));
  }, [isB2B, normalizedItems]);

  useEffect(() => {
    if (
      !isB2B &&
      activeTab === 'bread' &&
      availableTabs.length > 0 &&
      !availableTabs.some((tab) => tab.id === activeTab)
    ) {
      setActiveTab(availableTabs[0].id);
      setFilters(EMPTY_FILTERS);
    }
  }, [activeTab, availableTabs, isB2B]);

  const tabItems = useMemo(
    () =>
      (isB2B && activeTab === 'all') || (!isB2B && activeTab === 'other')
        ? normalizedItems
        : normalizedItems.filter((item) => item.tabId === activeTab),
    [activeTab, isB2B, normalizedItems]
  );

  const filterOptions = useMemo(() => {
    const collect = (key: FilterKey) =>
      Array.from(
        new Set(
          tabItems
            .map((item) => item[key])
            .filter((value): value is string => Boolean(value))
            .map((value) => titleCase(value) as string)
        )
      );

    return {
      brand: collect('brand'),
      type: collect('type'),
      color: collect('color'),
    };
  }, [tabItems]);

  const visibleItems = useMemo(() => {
    return tabItems
      .filter((item) => {
        if (filters.brand && titleCase(item.brand) !== filters.brand) {
          return false;
        }
        if (filters.type && titleCase(item.type) !== filters.type) {
          return false;
        }
        if (filters.color && titleCase(item.color) !== filters.color) {
          return false;
        }
        return true;
      })
      .sort((left, right) => left.item.name.localeCompare(right.item.name));
  }, [filters.brand, filters.color, filters.type, tabItems]);

  const selectedItemIds = useMemo(
    () =>
      new Set(
        draftItems.map((entry) => entry.item._id || entry.item.id).filter(Boolean) as string[]
      ),
    [draftItems]
  );

  const selectedItemQuantities = useMemo(() => {
    const quantities = new Map<string, number>();

    for (const draftItem of draftItems) {
      const id = draftItem.item._id || draftItem.item.id;
      if (id) {
        quantities.set(id, draftItem.quantity);
      }
    }

    return quantities;
  }, [draftItems]);

  const selectedVisibleItems = useMemo(
    () =>
      normalizedItems
        .filter((item) => selectedItemIds.has(item.id))
        .sort((left, right) => left.item.name.localeCompare(right.item.name)),
    [normalizedItems, selectedItemIds]
  );

  const unselectedVisibleItems = useMemo(
    () => visibleItems.filter((item) => !selectedItemIds.has(item.id)),
    [selectedItemIds, visibleItems]
  );

  const totalPrice = useMemo(
    () =>
      draftItems.reduce((sum, currentItem) => {
        const unitPrice = currentItem.subscriptionOption?.price ?? currentItem.item.price ?? 0;
        return sum + unitPrice * currentItem.quantity;
      }, 0),
    [draftItems]
  );

  const selectedLineCount = draftItems.length;
  const totalQuantity = useMemo(
    () => draftItems.reduce((sum, currentItem) => sum + currentItem.quantity, 0),
    [draftItems]
  );
  const minThreshold = subscription?.minThreshold ?? 0;
  const minQuantity = subscription?.minQuantity ?? 0;
  const remainingToThreshold = Math.max(0, minThreshold - totalPrice);
  const remainingToMinimumQuantity = Math.max(0, minQuantity - totalQuantity);
  const isBelowThreshold = isB2B ? remainingToMinimumQuantity > 0 : remainingToThreshold > 0;
  const recurringLabel = subscription?.frequency ? `/${subscription.frequency}` : '';
  const monthlyDeliveryCount = isB2B
    ? b2bDeliveryFrequency === 'Every Other Day'
      ? 13
      : 26
    : 4;
  const projectedMonthlyTotal = totalPrice * monthlyDeliveryCount;
  const minimumMet = isB2B ? totalQuantity >= minQuantity : totalPrice >= minThreshold;

  useEffect(() => {
    setCheckoutError(null);
  }, [activeTab, b2bDeliveryFrequency, draftItems, filters.brand, filters.color, filters.type]);

  useEffect(() => {
    void prefetchVendors('subscription');
  }, []);

  const updateQuantity = useCallback((displayItem: SubscriptionDisplayItem, nextQuantity: number) => {
    const itemId = displayItem.id;

    setDraftItems((currentItems) => {
      const currentIndex = currentItems.findIndex(
        (entry) => (entry.item._id || entry.item.id) === itemId
      );

      if (nextQuantity <= 0) {
        if (currentIndex === -1) {
          return currentItems;
        }

        return currentItems.filter((_, index) => index !== currentIndex);
      }

      if (currentIndex >= 0) {
        return currentItems.map((entry, index) =>
          index === currentIndex
            ? {
                ...entry,
                quantity: nextQuantity,
                subscriptionOption: buildSubscriptionOption(
                  entry.item,
                  subscription?.frequency,
                  displayItem.subtitle
                ),
              }
            : entry
        );
      }

      return [
        ...currentItems,
        {
          item: {
            ...displayItem.item,
            id: displayItem.item._id || displayItem.item.id,
            description: displayItem.subtitle,
          },
          quantity: nextQuantity,
          subscriptionOption: buildSubscriptionOption(
            displayItem.item,
            subscription?.frequency,
            displayItem.subtitle
          ),
        },
      ];
    });
  }, [subscription?.frequency]);

  const handleContinue = () => {
    if (isBelowThreshold) {
      setCheckoutError(
        isB2B
          ? `Add ${remainingToMinimumQuantity} more pieces to meet your tier minimum before continuing.`
          : `Add ${formatNaira(remainingToThreshold)} more to meet your plan minimum before continuing.`
      );
      return;
    }

    onUpdateItems(draftItems);
    onContinue();
  };

  const renderFilterRow = (filterKey: FilterKey, label: string) => (
    <div className="subscription-filter-row">
      <button
        type="button"
        className={`subscription-filter-chip subscription-filter-pill ${
          filters[filterKey] === null ? 'active' : ''
        }`}
        onClick={() => setFilters((current) => ({ ...current, [filterKey]: null }))}
      >
        {label}
      </button>

      {filterOptions[filterKey].map((value) => (
        <button
          key={`${filterKey}-${value}`}
          type="button"
          className={`subscription-filter-chip ${
            filters[filterKey] === value ? 'active-outline' : ''
          }`}
          onClick={() =>
            setFilters((current) => ({
              ...current,
              [filterKey]: current[filterKey] === value ? null : value,
            }))
          }
        >
          {value}
        </button>
      ))}
    </div>
  );

  const activeConsumerFilterKeys = useMemo(() => {
    if (isB2B) {
      return [] as FilterKey[];
    }

    if (activeTab === 'bread') {
      return ['brand'] as FilterKey[];
    }

    if (activeTab === 'water') {
      return ['brand', 'type'] as FilterKey[];
    }

    if (activeTab === 'other') {
      return [] as FilterKey[];
    }

    return ['brand', 'type', 'color'] as FilterKey[];
  }, [activeTab, isB2B]);

  return (
    <div
      className={`subscription-customize-page page-enter ${
        isB2B ? 'subscription-customize-page-b2b' : 'subscription-customize-page-consumer'
      }`}
    >
      <div className="subscription-customize-content">
        <div className="subscription-customize-header">
          <button type="button" className="subscription-back-button" onClick={onBack} aria-label="Go back">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div>
            <h1 className="subscription-customize-title">
              {isB2B ? 'Build Your Daily Mix' : 'Customize Your Plan'}
            </h1>
            <p className="subscription-customize-subtitle">
              {isB2B ? 'Adjust quantities within your tier' : 'Adjust quantities to fit your needs'}
            </p>
          </div>
        </div>

        <StepIndicator totalSteps={4} currentStep={1} />

        {isB2B ? (
          <div className={`subscription-b2b-status-card ${minimumMet ? 'met' : ''}`}>
            <div className="subscription-b2b-status-copy">
              <strong>{minimumMet ? 'Minimum Met' : 'Minimum Not Met'}</strong>
              <p>{minimumMet ? 'Ready to proceed' : `Add ${remainingToMinimumQuantity} more pieces`}</p>
            </div>
            <div className="subscription-b2b-status-count">
              <span>{totalQuantity}</span>
              <small>pieces/day</small>
            </div>
          </div>
        ) : null}

        {isB2B ? (
          <div className="subscription-b2b-controls">
            <div className="subscription-b2b-control-group">
              <label className="subscription-b2b-control-label">Delivery Frequency</label>
              <div className="subscription-tab-row subscription-tab-row-compact">
                {['Daily', 'Every Other Day'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`subscription-tab-chip subscription-tab-chip-compact ${b2bDeliveryFrequency === option ? 'active' : ''}`}
                    onClick={() => {
                      if (!subscription || !onSubscriptionChange) {
                        return;
                      }

                      onSubscriptionChange({
                        ...subscription,
                        deliveryFrequency: option,
                      });
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

          </div>
        ) : null}

        <div
          className={`subscription-tab-row ${
            isB2B
              ? 'subscription-tab-row-compact subscription-b2b-category-row'
              : 'subscription-tab-row-compact subscription-consumer-category-row'
          }`}
        >
          {availableTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`subscription-tab-chip subscription-tab-chip-compact ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setFilters(EMPTY_FILTERS);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!isB2B ? (
          <div className="subscription-filters-panel subscription-filters-panel-consumer">
            {activeConsumerFilterKeys.includes('brand')
              ? renderFilterRow('brand', 'All Brands')
              : null}
            {activeConsumerFilterKeys.includes('type')
              ? renderFilterRow('type', 'All Types')
              : null}
            {activeConsumerFilterKeys.includes('color')
              ? renderFilterRow('color', 'All Colors')
              : null}
          </div>
        ) : null}

        {error ? (
          <div className="subscription-inline-alert">
            <span>{error}</span>
            <button type="button" onClick={() => void refetch()}>
              Retry
            </button>
          </div>
        ) : null}

        {loading && visibleItems.length === 0 ? (
          <div className="subscription-state-card">
            <div className="subscription-spinner" />
            <p>Loading your plan items...</p>
          </div>
        ) : null}

        {!loading && visibleItems.length === 0 ? (
          <div className="subscription-state-card">
            <p>No items match this view yet.</p>
          </div>
        ) : null}

        <div className="subscription-item-list">
          {selectedVisibleItems.length > 0 ? (
            <div className="subscription-item-section-label">Added to cart</div>
          ) : null}

          {selectedVisibleItems.map((displayItem) => {
            const quantity = selectedItemQuantities.get(displayItem.id) ?? 0;

            return (
              <SubscriptionItemCard
                key={displayItem.id}
                displayItem={displayItem}
                quantity={quantity}
                isB2B={isB2B}
                onUpdateQuantity={updateQuantity}
              />
            );
          })}

          {selectedVisibleItems.length > 0 && unselectedVisibleItems.length > 0 ? (
            <div className="subscription-item-divider">
              <span>More items</span>
            </div>
          ) : null}

          {unselectedVisibleItems.map((displayItem) => {
            const quantity = selectedItemQuantities.get(displayItem.id) ?? 0;

            return (
              <SubscriptionItemCard
                key={displayItem.id}
                displayItem={displayItem}
                quantity={quantity}
                isB2B={isB2B}
                onUpdateQuantity={updateQuantity}
              />
            );
          })}
        </div>
      </div>

      <div className="subscription-footer">
        <div className="subscription-footer-copy">
          {isB2B ? (
            <div className="subscription-footer-summary-row">
              <span>Daily cost:</span>
              <strong>{formatNaira(totalPrice)}</strong>
            </div>
          ) : (
            <>
              <span>{`${selectedLineCount} items in cart`}</span>
              <strong>
                {formatNaira(totalPrice)}
                {recurringLabel}
              </strong>
            </>
          )}
          {isB2B ? (
            <small>Monthly total ({monthlyDeliveryCount} deliveries) {formatNaira(projectedMonthlyTotal)}</small>
          ) : isBelowThreshold ? (
            <small>Add {formatNaira(remainingToThreshold)} more to continue.</small>
          ) : (
            <small>Your plan minimum has been met.</small>
          )}
          {checkoutError ? <small className="error">{checkoutError}</small> : null}
        </div>

        <button type="button" className="subscription-footer-button" onClick={handleContinue}>
          {isB2B ? 'Checkout' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
